import { 
  Assembly, 
  AssemblyOverviewView,
  AssemblyMinutesStatus,
  AssemblyScheduledOwnerView,
  ConnectedParticipantsManagerView,
  AssemblySummaryManagerView,
  CreateAssemblyDto,
  UpdateAssemblyDto,
  RescheduleAssemblyDto,
  AssemblyFilterDto,
  AssemblyStats
} from '../types/assembly';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class AssemblyService {
  private getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getAssemblies(filters?: AssemblyFilterDto): Promise<{
    assemblies: Assembly[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const url = `${API_BASE_URL}/assemblies${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{
      success: boolean;
      data: {
        assemblies: Assembly[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>(response);
    return result.data;
  }

  async getOwnerScheduledAssembliesView(limit: number = 50): Promise<AssemblyScheduledOwnerView[]> {
    const response = await fetch(`${API_BASE_URL}/assemblies/owner/scheduled-view?limit=${limit}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyScheduledOwnerView[] }>(response);
    return result.data;
  }

  async getAssemblyById(id: string): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: Assembly }>(response);
    return result.data;
  }

  async getAssemblyOverviewView(id: string): Promise<AssemblyOverviewView> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/overview-view`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyOverviewView }>(response);
    return result.data;
  }

  /**
   * Obtiene una asamblea por ID sin autenticación (para enlaces públicos)
   */
  async getAssemblyByIdPublic(id: string): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const result = await this.handleResponse<{ success: boolean; data: Assembly }>(response);
    return result.data;
  }

  async getAssemblyByCode(code: string): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/code/${code}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: Assembly }>(response);
    return result.data;
  }

  async createAssembly(assemblyData: CreateAssemblyDto): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(assemblyData),
    });
    const result = await this.handleResponse<{ success: boolean; message: string; data: Assembly }>(response);
    return result.data;
  }

  async updateAssembly(id: string, assemblyData: UpdateAssemblyDto): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(assemblyData),
    });
    const result = await this.handleResponse<{ success: boolean; message: string; data: Assembly }>(response);
    return result.data;
  }

  async deleteAssembly(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  async startAssembly(id: string): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/start`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; message: string; data: Assembly }>(response);
    return result.data;
  }

  async completeAssembly(id: string): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/complete`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; message: string; data: Assembly }>(response);
    return result.data;
  }

  async rescheduleAssembly(id: string, rescheduleData: RescheduleAssemblyDto): Promise<Assembly> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/reschedule`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(rescheduleData),
    });
    const result = await this.handleResponse<{ success: boolean; message: string; data: Assembly }>(response);
    return result.data;
  }

  async downloadMinutes(assemblyId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/minutes/download`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  async getMinutesStatus(assemblyId: string): Promise<AssemblyMinutesStatus> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/minutes/status`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyMinutesStatus }>(response);
    return result.data;
  }

  async queueMinutesGeneration(assemblyId: string): Promise<AssemblyMinutesStatus> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/minutes/queue`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyMinutesStatus }>(response);
    return result.data;
  }

  async regenerateMinutes(assemblyId: string): Promise<AssemblyMinutesStatus> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/minutes/regenerate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyMinutesStatus }>(response);
    return result.data;
  }

  async downloadMinutesAttendeesExcel(assemblyId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/minutes/attendees/download`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  async downloadAssemblyChatPdf(assemblyId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/chat/download`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  async getAssemblyStats(id: string): Promise<AssemblyStats> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${id}/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblyStats }>(response);
    return result.data;
  }

  async getConnectedParticipants(assemblyId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/participants`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: any[] }>(response);
    return result.data;
  }

  async getConnectedParticipantsManagerView(assemblyId: string): Promise<ConnectedParticipantsManagerView> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/participants/manager-view`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: ConnectedParticipantsManagerView }>(response);
    return result.data;
  }

  async getAssemblySummaryManagerView(assemblyId: string): Promise<AssemblySummaryManagerView> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/summary/manager-view`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: AssemblySummaryManagerView }>(response);
    return result.data;
  }

  async joinAssembly(assemblyId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/join`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: any }>(response);
    return result.data;
  }

  async leaveAssembly(assemblyId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/leave`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: any }>(response);
    return result.data;
  }

  async resendInvitations(assemblyId: string, forceResendDelivered: boolean = false): Promise<{
    sent?: number;
    failed?: number;
    skipped?: number;
    started?: boolean;
    assemblyId?: string;
    users?: Array<{ userId: string; email: string; success: boolean; error?: string }>;
  }> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/resend-invitations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ forceResendDelivered }),
    });
    const result = await this.handleResponse<{
      success: boolean;
      message: string;
      data: {
        sent?: number;
        failed?: number;
        skipped?: number;
        started?: boolean;
        assemblyId?: string;
        forceResendDelivered?: boolean;
        users?: Array<{ userId: string; email: string; success: boolean; error?: string }>;
      };
    }>(response);

    return result.data;
  }

  async sendReminders(assemblyId: string): Promise<{
    started?: boolean;
    assemblyId?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/assemblies/${assemblyId}/send-reminders`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{
      success: boolean;
      message: string;
      data: {
        started?: boolean;
        assemblyId?: string;
      };
    }>(response);

    return result.data;
  }

  async getMyActiveAssembly(): Promise<Assembly | null> {
    const response = await fetch(`${API_BASE_URL}/assemblies/active/my`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    const result = await this.handleResponse<{ success: boolean; data: Assembly | null }>(response);
    return result.data;
  }
}

export const assemblyService = new AssemblyService();